import { useSavedInRef } from '@coop/hooks';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import type { ApiProduct } from '../../../models/ecommerce/product/ApiProduct';
import { productFlow } from '../../common/flow/product/productFlow';
import { useAppSelector } from '../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../common/selectors/storeSelectors';
import recipeFlow from '../flow/recipeFlow';
import type { RecipeItemState } from '../models';

const loadHybrisSubstitutes = async (storeId: string, substituteIdentifier: string) => {
    const recipeProducts = await recipeFlow.getSubstitutes(storeId, substituteIdentifier);
    const products =
        (await productFlow.getProductsByIds(
            recipeProducts
                .filter((substitute) => substitute.purchasable)
                .map((substitute) => substitute.id),
            storeId,
        )) || [];

    return {
        substituteIdentifier,
        substitutes: products,
    };
};

export const useSubstitutes = (
    onSubstituteChangeClick: (item: RecipeItemState, product: ApiProduct) => void,
    allItems?: RecipeItemState[],
    onSubstituteProductSuccess?: (changedSubstituteIdentifier: string) => void,
    substituteIdentifier?: string,
) => {
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const subsitutesQuery = useQuery({
        queryKey: ['useSubstitutes', currentProductionUnit, substituteIdentifier],
        queryFn: () => loadHybrisSubstitutes(currentProductionUnit, substituteIdentifier!),
        enabled: !!substituteIdentifier,
    });

    const substituteSentForChange = useRef<{
        substituteIdentifier: string;
        productId: string;
    }>(null);
    const onSuccess = useSavedInRef(onSubstituteProductSuccess);

    // since we dont have a substitute change endpoint like in cart, we need to wait for the resolve to return,
    // this is not ideal, we then try to see if its in the returned items
    useEffect(() => {
        if (
            !!substituteSentForChange.current &&
            allItems?.find(
                (item) =>
                    item.id === substituteSentForChange.current?.productId &&
                    item.substituteIdentifier ===
                        substituteSentForChange.current.substituteIdentifier,
            )
        ) {
            onSuccess.current?.(substituteSentForChange.current.substituteIdentifier);
        }
        substituteSentForChange.current = null;
    }, [onSuccess, allItems]);

    const hanleSubstituteChangeClick = useCallback(
        (item: RecipeItemState, product: ApiProduct) => {
            substituteSentForChange.current = {
                substituteIdentifier: item.substituteIdentifier,
                productId: product.identifier,
            };
            onSubstituteChangeClick(item, product);
        },
        [onSubstituteChangeClick],
    );

    return {
        onSubstituteChangeClick: hanleSubstituteChangeClick,
        subsitutesQuery,
    };
};
